import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "TarjetaUID";

const ListarTarjetasAsignadas = ({ refresh }) => {
  const [tarjetas, setTarjetas] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const normalizarTexto = (texto = "") =>
    texto
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "")
      .toLowerCase();

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const obtenerPermisosModulo = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      return (
        modulos.find((modulo) => {
          const nombreModulo =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombreModulo) === normalizarTexto(MODULO);
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const permisosModulo = obtenerPermisosModulo();

  const puedeCrear =
    permisosModulo?.puedeCrear === true || permisosModulo?.crear === true;

  const puedeActualizar =
    permisosModulo?.puedeActualizar === true ||
    permisosModulo?.actualizar === true;

  const puedeEliminar =
    permisosModulo?.puedeEliminar === true ||
    permisosModulo?.eliminar === true;

  const desactivarTarjeta = useCallback(
    async (id) => {
      if (!puedeEliminar && !puedeActualizar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para desactivar tarjetas.",
          "warning"
        );
        return;
      }

      try {
        const confirm = await Swal.fire({
          title: "¿Está seguro?",
          text: "La tarjeta será desactivada.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, desactivar",
          cancelButtonText: "Cancelar",
        });

        if (confirm.isConfirmed) {
          await axios.put(`/api/tarjetas/desactivar/${id}`, {}, authConfig());

          await getData();

          Swal.fire({
            icon: "success",
            title: "Tarjeta desactivada correctamente",
            showConfirmButton: false,
            timer: 1500,
          });
        }
      } catch (error) {
        console.error("Error al desactivar la tarjeta:", error);

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo desactivar la tarjeta.",
          "error"
        );
      }
    },
    [puedeEliminar, puedeActualizar]
  );

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get("/api/tarjetas/listall", authConfig());

      if (!data.ok) throw new Error(data.message);

      const tarjetasConFunciones = (data.asignaciones || []).map((tarjeta) => ({
        ...tarjeta,

        documento: tarjeta.aprendizObjectId?.documentoAprendiz || "N/A",

        nombre: `${tarjeta.aprendizObjectId?.nombre1Aprendiz || ""} ${
          tarjeta.aprendizObjectId?.apellido1Aprendiz || ""
        }`.trim(),

        fechaAsignacion: tarjeta.fechaAsignacion
          ? new Date(tarjeta.fechaAsignacion).toLocaleDateString("es-CO")
          : "-",

        estado: tarjeta.estado,

        funciones: (
          <div className="d-flex align-items-center justify-content-end gap-2 flex-nowrap">
            <button
              className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
              style={{
                width: 34,
                height: 34,
                opacity: puedeEliminar || puedeActualizar ? 1 : 0.5,
                cursor:
                  puedeEliminar || puedeActualizar ? "pointer" : "not-allowed",
              }}
              disabled={!puedeEliminar && !puedeActualizar}
              onClick={() => desactivarTarjeta(tarjeta._id)}
              title={
                puedeEliminar || puedeActualizar
                  ? "Desactivar Tarjeta"
                  : "Sin permiso para desactivar"
              }
              type="button"
            >
              <i className="fa-solid fa-ban"></i>
            </button>
          </div>
        ),
      }));

      setTarjetas(tarjetasConFunciones.reverse());
    } catch (error) {
      console.error("Error al cargar las tarjetas asignadas:", error);

      setTarjetas([]);

      Swal.fire(
        "Error",
        error.response?.data?.message || "No se pudieron cargar las tarjetas.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [desactivarTarjeta, puedeActualizar, puedeEliminar]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  const columns = useMemo(
    () => [
      { Header: "Documento", accessor: "documento" },
      {
        Header: "Nombre Aprendiz",
        accessor: "nombre",
        width: 280,
        hideHeaderOnMobile: true,
      },
      { Header: "UID Tarjeta", accessor: "tarjetaUID" },
      { Header: "Fecha Asignación", accessor: "fechaAsignacion" },
      { Header: "Estado", accessor: "estado" },
      { accessor: "funciones" },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Tarjetas UID</h5>
                <small className="text-secondary">
                  Control y administración de tarjetas asignadas a aprendices.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                <button
                  className="btn btn-dark"
                  type="button"
                  disabled={!puedeCrear}
                  style={{
                    opacity: puedeCrear ? 1 : 0.5,
                    cursor: puedeCrear ? "pointer" : "not-allowed",
                  }}
                  onClick={() => {
                    if (!puedeCrear) {
                      Swal.fire(
                        "Sin permisos",
                        "No tiene permisos para asignar tarjetas.",
                        "warning"
                      );
                      return;
                    }

                    navigate("/tarjeta/nuevo");
                  }}
                  title={
                    puedeCrear
                      ? "Asignar nueva tarjeta"
                      : "Sin permiso para crear"
                  }
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Nuevo
                </button>
              </div>
            </div>
          </div>

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>
                <div className="mt-3">Cargando tarjetas asignadas…</div>
              </div>
            ) : Array.isArray(tarjetas) && tarjetas.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={tarjetas} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">💳</div>
                <div className="fw-semibold">No hay tarjetas asignadas</div>
                <div className="small mb-3">
                  No se encontraron tarjetas activas en el sistema.
                </div>

                <button
                  className="btn btn-dark"
                  type="button"
                  disabled={!puedeCrear}
                  style={{
                    opacity: puedeCrear ? 1 : 0.5,
                    cursor: puedeCrear ? "pointer" : "not-allowed",
                  }}
                  onClick={() => {
                    if (!puedeCrear) {
                      Swal.fire(
                        "Sin permisos",
                        "No tiene permisos para asignar tarjetas.",
                        "warning"
                      );
                      return;
                    }

                    navigate("/tarjeta/nuevo");
                  }}
                  title={
                    puedeCrear
                      ? "Asignar nueva tarjeta"
                      : "Sin permiso para crear"
                  }
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Asignar tarjeta
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarTarjetasAsignadas;